import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  Checkbox,
  FormControl,
  FormControlLabel,
  Grid,
  Link,
  MenuItem,
  TextField,
  Typography
} from "@mui/material";
import { ROLES } from "../../auth/constants.js";
import { getContactTypes } from "../../facilityManagement/data.js";
import {
  deleteDistributionList,
  getDistributionLists,
  saveDistributionList,
  SHARELIST
} from "../data/distributionList.js";
import { getEmailDistributionList } from "../data/index.js";
import PillsAutocomplete from "../PillsAutocomplete.js";

const roleOptions = Object.values(ROLES).sort((a, b) => a.name.localeCompare(b.name))
const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const emptyDistribution = () => ({
  roles: [],
  ownershipContactTypes: [],
  facilityContactTypes: [],
  emailList: [],
  emailListInvalid: false
})

const parseEmailList = (value) =>
  value
    .split(/[,;\n]/)
    .map((email) => email.trim())
    .filter(Boolean);

const getInvalidEmails = (emails) =>
  emails.filter((email) => !emailRegex.test(email));

const normalizeDistributionListName = (name) => String(name ?? "").trim();

const areEmailListsEqual = (left, right) => {
  if (left.length !== right.length) return false;
  return left.every((email, index) => email === right[index]);
};

const getEmailListValidation = (value) => {
  if (!value.trim()) {
    return {
      emailList: [],
      invalidEmails: [],
      hasExtraDelimiters: false
    };
  }

  const delimiterRegex = /[,;\n]/;
  const parts = value.split(delimiterRegex);
  const emailList = parts.map((email) => email.trim()).filter(Boolean);
  const trimmedValue = value.trim();
  const hasTrailingDelimiter = delimiterRegex.test(trimmedValue.slice(-1));
  let allowedTrailingEmptyEntryUsed = false;

  const emptyEntryCount = parts.reduce((count, part, index) => {
    if (part.trim()) return count;

    if (
      hasTrailingDelimiter
      && index === parts.length - 1
      && emailList.length > 0
      && !allowedTrailingEmptyEntryUsed
    ) {
      allowedTrailingEmptyEntryUsed = true;
      return count;
    }

    return count + 1;
  }, 0);

  return {
    emailList,
    invalidEmails: getInvalidEmails(emailList),
    hasExtraDelimiters: emptyEntryCount > 0
  };
};

const normalizeEmailListResponse = (response) => {
  const emailList =
    Array.isArray(response) ? response
    : Array.isArray(response?.emailList) ? response.emailList
    : Array.isArray(response?.emails) ? response.emails
    : Array.isArray(response?.recipients) ? response.recipients
    : typeof response === "string" ? parseEmailList(response)
    : [];

  return emailList
    .map((email) => {
      if (typeof email === "string") return email;
      return email?.email
        ?? email?.mail
        ?? email?.emailAddress
        ?? email?.address
        ?? email?.userPrincipalName
        ?? "";
    })
    .map((email) => email.trim())
    .filter(Boolean);
};

export default function ShareArticleDistributionControls({ open, onDistributionChange }) {
  const [rolesEnabled, setRolesEnabled] = useState(false);
  const [ownershipEnabled, setOwnershipEnabled] = useState(false);
  const [facilityEnabled, setFacilityEnabled] = useState(false);
  const [savedDistributionListsEnabled, setSavedDistributionListsEnabled] = useState(false);
  const [distributionListVisible, setDistributionListVisible] = useState(false);

  /** selected values are OBJECTS */
  const [ownershipContacts, setOwnershipContacts] = useState([]);
  const [facilityContacts, setFacilityContacts] = useState([]);
  const [selectedRoles, setSelectedRoles] = useState([]);
  const [savedDistributionLists, setSavedDistributionLists] = useState([]);
  const [selectedSavedDistributionListId, setSelectedSavedDistributionListId] = useState("");
  const [distributionListName, setDistributionListName] = useState("");
  const [distributionListText, setDistributionListText] = useState("");
  const [invalidEmails, setInvalidEmails] = useState([]);
  const [hasExtraEmailDelimiters, setHasExtraEmailDelimiters] = useState(false);
  const [distributionListError, setDistributionListError] = useState("");
  const [distributionListLoading, setDistributionListLoading] = useState(false);
  const [savedDistributionListsLoading, setSavedDistributionListsLoading] = useState(false);
  const [distributionListSaving, setDistributionListSaving] = useState(false);
  const [distributionListDeleting, setDistributionListDeleting] = useState(false);

  const [facilityContactTypes, setFacilityContactTypes] = useState([]);
  const [ownershipContactTypes, setOwnershipContactTypes] = useState([]);

  const notifyDistributionChange = ({
    roles = selectedRoles,
    ownership = ownershipContacts,
    facility = facilityContacts,
    emailListText = distributionListText,
    invalidEmailList = invalidEmails,
    hasInvalidEmailDelimiters = hasExtraEmailDelimiters
  } = {}) => {
    const emailList = parseEmailList(emailListText);

    onDistributionChange({
      roles: roles.map((role) => role.id),
      ownershipContactTypes: ownership.map((o) => o.id),
      facilityContactTypes: facility.map((f) => f.id),
      emailList,
      emailListInvalid: invalidEmailList.length > 0 || hasInvalidEmailDelimiters
    });
  };

  const clearDistributionList = ({
    roles = selectedRoles,
    ownership = ownershipContacts,
    facility = facilityContacts,
    clearSavedSelection = true,
    clearSavedEnabled = true
  } = {}) => {
    setDistributionListText("");
    setDistributionListName("");
    setInvalidEmails([]);
    setHasExtraEmailDelimiters(false);
    setDistributionListError("");
    setDistributionListVisible(false);
    if (clearSavedSelection) {
      setSelectedSavedDistributionListId("");
    }
    if (clearSavedEnabled) {
      setSavedDistributionListsEnabled(false);
    }
    notifyDistributionChange({
      roles,
      ownership,
      facility,
      emailListText: "",
      invalidEmailList: [],
      hasInvalidEmailDelimiters: false
    });
  };

  const getSelectedCriteria = ({
    roles = selectedRoles,
    ownership = ownershipContacts,
    facility = facilityContacts
  } = {}) => ({
    roles: roles.map((role) => role.id),
    ownershipContactTypes: ownership.map((o) => o.id),
    facilityContactTypes: facility.map((f) => f.id)
  });

  const hasSelectedCriteria = ({
    roles = selectedRoles,
    ownership = ownershipContacts,
    facility = facilityContacts
  } = {}) =>
    roles.length > 0 || ownership.length > 0 || facility.length > 0;

  const clearCriteriaSelections = () => {
    setRolesEnabled(false);
    setOwnershipEnabled(false);
    setFacilityEnabled(false);
    setSelectedRoles([]);
    setOwnershipContacts([]);
    setFacilityContacts([]);
  };

  const loadSavedDistributionLists = async () => {
    setSavedDistributionListsLoading(true);
    setDistributionListError("");

    try {
      const lists = await getDistributionLists(SHARELIST);
      setSavedDistributionLists(lists);
      return lists;
    } catch (err) {
      setDistributionListError(err.message);
      return [];
    } finally {
      setSavedDistributionListsLoading(false);
    }
  };

  useEffect(() => {
    if (open) return;
    setRolesEnabled(false);
    setOwnershipEnabled(false);
    setFacilityEnabled(false);
    setSavedDistributionListsEnabled(false);
    setDistributionListVisible(false);
    setOwnershipContacts([]);
    setFacilityContacts([]);
    setSelectedRoles([]);
    setSavedDistributionLists([]);
    setSelectedSavedDistributionListId("");
    setDistributionListName("");
    setDistributionListText("");
    setInvalidEmails([]);
    setHasExtraEmailDelimiters(false);
    setDistributionListError("");
    setDistributionListLoading(false);
    setSavedDistributionListsLoading(false);
    setDistributionListSaving(false);
    setDistributionListDeleting(false);
    onDistributionChange(emptyDistribution());
  }, [onDistributionChange, open]);

  useEffect(() => {
    if (!open) return;

    let isActive = true;

    (async () => {
      const [ownership, facility] = await Promise.all([
        getContactTypes("ownership", false),
        getContactTypes("manufacturer", false)
      ]);

      if (!isActive) return;

      setOwnershipContactTypes(
        ownership.filter(ct => !ct.isInternal && ct.name !== 'Other')
      );
      setFacilityContactTypes(
        facility.filter(ct => !ct.isInternal && ct.name !== 'Other')
      );
    })();

    return () => {
      isActive = false;
    }
  }, [open]);

  const handleRoleSelect = (nextRoles) => {
    setSelectedRoles(nextRoles);
    clearDistributionList({ roles: nextRoles });
  };

  const handleOwnershipSelect = (nextOwnershipContacts) => {
    setOwnershipContacts(nextOwnershipContacts);
    clearDistributionList({ ownership: nextOwnershipContacts });
  };

  const handleFacilitySelect = (nextFacilityContacts) => {
    setFacilityContacts(nextFacilityContacts);
    clearDistributionList({ facility: nextFacilityContacts });
  };

  const handleRolesEnabledChange = (event) => {
    const nextRolesEnabled = event.target.checked;
    setRolesEnabled(nextRolesEnabled);

    if (!nextRolesEnabled) {
      setSelectedRoles([]);
      clearDistributionList({ roles: [] });
    } else {
      clearDistributionList({ roles: [] });
    }
  };

  const handleOwnershipEnabledChange = (event) => {
    const nextOwnershipEnabled = event.target.checked;
    setOwnershipEnabled(nextOwnershipEnabled);

    if (!nextOwnershipEnabled) {
      setOwnershipContacts([]);
      clearDistributionList({ ownership: [] });
    } else {
      clearDistributionList({ ownership: [] });
    }
  };

  const handleFacilityEnabledChange = (event) => {
    const nextFacilityEnabled = event.target.checked;
    setFacilityEnabled(nextFacilityEnabled);

    if (!nextFacilityEnabled) {
      setFacilityContacts([]);
      clearDistributionList({ facility: [] });
    } else {
      clearDistributionList({ facility: [] });
    }
  };

  const handleSavedDistributionListsEnabledChange = async (event) => {
    const nextSavedDistributionListsEnabled = event.target.checked;
    setSavedDistributionListsEnabled(nextSavedDistributionListsEnabled);

    if (nextSavedDistributionListsEnabled) {
      clearCriteriaSelections();
      clearDistributionList({
        roles: [],
        ownership: [],
        facility: [],
        clearSavedEnabled: false
      });
      await loadSavedDistributionLists();
    } else {
      clearDistributionList({ clearSavedEnabled: false });
    }
  };

  const handleSavedDistributionListSelect = (event) => {
    const nextSelectedSavedDistributionListId = event.target.value;
    const selectedDistributionList = savedDistributionLists.find(
      (list) => list.id === nextSelectedSavedDistributionListId
    );

    setSelectedSavedDistributionListId(nextSelectedSavedDistributionListId);

    if (!selectedDistributionList) {
      setDistributionListName("");
      clearDistributionList({
        roles: [],
        ownership: [],
        facility: [],
        clearSavedSelection: false,
        clearSavedEnabled: false
      });
      return;
    }

    const nextDistributionListText = selectedDistributionList.emailList.join(", ");
    const nextInvalidEmails = getInvalidEmails(selectedDistributionList.emailList);

    clearCriteriaSelections();
    setDistributionListName(selectedDistributionList.name);
    setDistributionListText(nextDistributionListText);
    setInvalidEmails(nextInvalidEmails);
    setHasExtraEmailDelimiters(false);
    setDistributionListError("");
    setDistributionListVisible(true);
    notifyDistributionChange({
      roles: [],
      ownership: [],
      facility: [],
      emailListText: nextDistributionListText,
      invalidEmailList: nextInvalidEmails,
      hasInvalidEmailDelimiters: false
    });
  };

  const handleDistributionListTextChange = (event) => {
    const nextDistributionListText = event.target.value;
    const validation = getEmailListValidation(nextDistributionListText);

    setDistributionListText(nextDistributionListText);
    setInvalidEmails(validation.invalidEmails);
    setHasExtraEmailDelimiters(validation.hasExtraDelimiters);
    setDistributionListError("");
    notifyDistributionChange({
      emailListText: nextDistributionListText,
      invalidEmailList: validation.invalidEmails,
      hasInvalidEmailDelimiters: validation.hasExtraDelimiters
    });
  };

  const handleViewDistributionList = async () => {
    setSavedDistributionListsEnabled(false);
    setSelectedSavedDistributionListId("");
    setDistributionListName("");
    setDistributionListLoading(true);
    setDistributionListError("");

    try {
      const response = await getEmailDistributionList(getSelectedCriteria());
      const emailList = normalizeEmailListResponse(response);
      const nextDistributionListText = emailList.join(", ");
      const nextInvalidEmails = getInvalidEmails(emailList);

      setDistributionListText(nextDistributionListText);
      setInvalidEmails(nextInvalidEmails);
      setHasExtraEmailDelimiters(false);
      setDistributionListVisible(true);
      notifyDistributionChange({
        emailListText: nextDistributionListText,
        invalidEmailList: nextInvalidEmails,
        hasInvalidEmailDelimiters: false
      });
    } catch (err) {
      setDistributionListError(err.message);
    } finally {
      setDistributionListLoading(false);
    }
  };

  const handleSaveDistributionList = async () => {
    const validation = getEmailListValidation(distributionListText);
    const normalizedDistributionListText = validation.emailList.join(", ");

    setInvalidEmails(validation.invalidEmails);
    setHasExtraEmailDelimiters(validation.hasExtraDelimiters);
    notifyDistributionChange({
      emailListText: distributionListText,
      invalidEmailList: validation.invalidEmails,
      hasInvalidEmailDelimiters: validation.hasExtraDelimiters
    });

    if (validation.hasExtraDelimiters) {
      setDistributionListError("Remove extra commas, semicolons, or blank email entries before saving the Distribution List.");
      return;
    }

    if (validation.invalidEmails.length) {
      setDistributionListError("Fix invalid emails before saving the Distribution List.");
      return;
    }

    setDistributionListText(normalizedDistributionListText);
    notifyDistributionChange({
      emailListText: normalizedDistributionListText,
      invalidEmailList: [],
      hasInvalidEmailDelimiters: false
    });
    setDistributionListSaving(true);
    setDistributionListError("");

    try {
      const nextSavedDistributionLists = await saveDistributionList(
        SHARELIST,
        {
          id: selectedSavedDistributionListId || undefined,
          name: distributionListName,
          emailList: validation.emailList
        }
      );
      const savedDistributionList = nextSavedDistributionLists.find(
        (list) =>
          list.id === selectedSavedDistributionListId
          || list.name.toLowerCase() === distributionListName.trim().toLowerCase()
      );

      setSavedDistributionLists(nextSavedDistributionLists);
      setSavedDistributionListsEnabled(true);
      setSelectedSavedDistributionListId(savedDistributionList?.id ?? "");
      setDistributionListName(savedDistributionList?.name ?? distributionListName.trim());
      clearCriteriaSelections();
    } catch (err) {
      setDistributionListError(err.message);
    } finally {
      setDistributionListSaving(false);
    }
  };

  const handleDeleteDistributionList = async () => {
    const selectedDistributionList = savedDistributionLists.find(
      (list) => list.id === selectedSavedDistributionListId
    );
    if (!selectedDistributionList) return;

    if (!window.confirm(`Delete Distribution List "${selectedDistributionList.name}"?`)) {
      return;
    }

    setDistributionListDeleting(true);
    setDistributionListError("");

    try {
      const nextSavedDistributionLists = await deleteDistributionList(
        SHARELIST,
        selectedDistributionList
      );

      setSavedDistributionLists(nextSavedDistributionLists);
      clearDistributionList();
    } catch (err) {
      setDistributionListError(err.message);
    } finally {
      setDistributionListDeleting(false);
    }
  };

  const invalidEmailHelperText = invalidEmails.length
    ? `Invalid email${invalidEmails.length === 1 ? "" : "s"}: ${invalidEmails.join(", ")}`
    : hasExtraEmailDelimiters
      ? "Remove extra commas, semicolons, or blank email entries."
    : "Emails must be comma-delimited.";

  const selectedSavedDistributionList = savedDistributionLists.find(
    (list) => list.id === selectedSavedDistributionListId
  );
  const currentDistributionListValidation = getEmailListValidation(distributionListText);
  const normalizedDistributionListText = currentDistributionListValidation.emailList.join(", ");
  const existingDistributionListHasChanges = selectedSavedDistributionList
    ? normalizeDistributionListName(distributionListName) !== selectedSavedDistributionList.name
      || distributionListText.trim() !== normalizedDistributionListText
      || !areEmailListsEqual(
        currentDistributionListValidation.emailList,
        selectedSavedDistributionList.emailList
      )
    : true;
  const canSaveDistributionList =
    !distributionListSaving
    && Boolean(normalizeDistributionListName(distributionListName))
    && currentDistributionListValidation.emailList.length > 0
    && invalidEmails.length === 0
    && !hasExtraEmailDelimiters
    && existingDistributionListHasChanges;

  const canDeleteDistributionList =
    Boolean(selectedSavedDistributionListId)
    && !distributionListDeleting;

  return (
    <Grid container spacing={2} alignItems="flex-start">
      <Grid item xs={12} md={7}>
        {/* ROLES */}
        <Box sx={{ mb: 1 }}>
          <FormControlLabel
            control={
              <Checkbox
                size="small"
                checked={rolesEnabled}
                onChange={handleRolesEnabledChange}
              />
            }
            label="Specific Role(s)"
          />

          {rolesEnabled && (
            <Box sx={{ pl: 4, width: "100%", boxSizing: "border-box" }}>
              <FormControl fullWidth size="small">
                <PillsAutocomplete
                  label="Specific Role(s)"
                  options={roleOptions}
                  value={selectedRoles}
                  setValue={handleRoleSelect}
                />
              </FormControl>
            </Box>
          )}
        </Box>

        {/* OWNERSHIP */}
        <Box sx={{ mb: 1 }}>
          <FormControlLabel
            control={
              <Checkbox
                size="small"
                checked={ownershipEnabled}
                onChange={handleOwnershipEnabledChange}
              />
            }
            label="Ownership Contact Types"
          />

          {ownershipEnabled && (
            <Box sx={{ pl: 4, width: "100%", boxSizing: "border-box" }}>
              <FormControl fullWidth size="small">
                <PillsAutocomplete
                  label="Ownership Contacts"
                  options={ownershipContactTypes}
                  value={ownershipContacts}
                  setValue={handleOwnershipSelect}
                />
              </FormControl>
            </Box>
          )}
        </Box>

        {/* FACILITY */}
        <Box sx={{ mb: 1 }}>
          <FormControlLabel
            control={
              <Checkbox
                size="small"
                checked={facilityEnabled}
                onChange={handleFacilityEnabledChange}
              />
            }
            label="Facility Contact Types"
          />

          {facilityEnabled && (
            <Box sx={{ pl: 4, width: "100%", boxSizing: "border-box" }}>
              <FormControl fullWidth size="small">
                <PillsAutocomplete
                  label="Facility Contacts"
                  options={facilityContactTypes}
                  value={facilityContacts}
                  setValue={handleFacilitySelect}
                />
              </FormControl>
            </Box>
          )}
        </Box>

        {/* SAVED DISTRIBUTION LIST */}
        <Box sx={{ mb: 1 }}>
          <FormControlLabel
            control={
              <Checkbox
                size="small"
                checked={savedDistributionListsEnabled}
                onChange={handleSavedDistributionListsEnabledChange}
              />
            }
            label="Distribution List"
          />

          {savedDistributionListsEnabled && (
            <Box sx={{ pl: 4, width: "100%", boxSizing: "border-box" }}>
              <TextField
                select
                fullWidth
                size="small"
                label="Existing Distribution List"
                value={selectedSavedDistributionListId}
                onChange={handleSavedDistributionListSelect}
                disabled={savedDistributionListsLoading}
                helperText={
                  savedDistributionListsLoading
                    ? "Loading Distribution Lists..."
                    : "Select an existing Distribution List or save the current emails as a new one."
                }
              >
                <MenuItem value="">
                  <em>None</em>
                </MenuItem>
                {savedDistributionLists.map((distributionList) => (
                  <MenuItem key={distributionList.id} value={distributionList.id}>
                    {distributionList.name}
                  </MenuItem>
                ))}
              </TextField>
            </Box>
          )}
        </Box>
      </Grid>

      <Grid item xs={12} md={5}>
        <Box
          sx={{
            border: 1,
            borderColor: "divider",
            borderRadius: 1,
            p: 2,
            width: "100%",
            boxSizing: "border-box"
          }}
        >
          {!savedDistributionListsEnabled && (
            <Button
              size="small"
              variant="outlined"
              onClick={handleViewDistributionList}
              disabled={!hasSelectedCriteria() || distributionListLoading}
              fullWidth
              sx={{ mb: 1 }}
            >
              {distributionListLoading ? "Loading Distribution List..." : "View the Distribution List"}
            </Button>
          )}

          {distributionListVisible ? (
            <Box sx={{ width: "100%", boxSizing: "border-box" }}>
              <TextField
                value={distributionListText}
                onChange={handleDistributionListTextChange}
                fullWidth
                multiline
                minRows={4}
                size="small"
                error={invalidEmails.length > 0 || hasExtraEmailDelimiters || Boolean(distributionListError)}
                helperText={
                  distributionListError
                    || (!hasSelectedCriteria() && !selectedSavedDistributionListId
                      ? "Select at least one role or contact type before viewing the distribution list."
                      : invalidEmailHelperText)
                }
              />
              <TextField
                label="Distribution List Name"
                value={distributionListName}
                onChange={(event) => {
                  setDistributionListName(event.target.value);
                  setDistributionListError("");
                }}
                size="small"
                fullWidth
                sx={{ mt: 1 }}
                helperText="Enter a name to save these emails as a Distribution List."
              />
              <Box sx={{ display: "flex", gap: 1, mt: 1, alignItems: "center" }}>
                <Link
                  component="button"
                  type="button"
                  underline="hover"
                  color={canDeleteDistributionList ? "error" : "text.disabled"}
                  onClick={handleDeleteDistributionList}
                  disabled={!canDeleteDistributionList}
                  sx={{
                    fontSize: "0.8125rem",
                    pointerEvents: canDeleteDistributionList ? "auto" : "none",
                    cursor: canDeleteDistributionList ? "pointer" : "default",
                    mr: "auto"
                  }}
                >
                  {distributionListDeleting ? "Deleting..." : "Delete List"}
                </Link>
                <Button
                  size="small"
                  variant="contained"
                  onClick={handleSaveDistributionList}
                  disabled={!canSaveDistributionList}
                  sx={{ whiteSpace: "nowrap" }}
                >
                  {distributionListSaving ? "Saving..." : "Save List"}
                </Button>
              </Box>
            </Box>
          ) : (
            <Typography variant="body2" color="text.secondary">
              {savedDistributionListsEnabled
                ? "Select an existing Distribution List to use its saved emails."
                : "Select role or contact type criteria, then view and optionally save the generated emails as a Distribution List."}
            </Typography>
          )}
        </Box>
      </Grid>
    </Grid>
  );
}
